/**
 * Main AskAsper chat component
 * Handles landing page and chat interface switching.
 * Uses AgenticChatMessage contract; two-panel layout derives state from messages + loadingMessage.
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import { AgenticTwoPanelLayout } from 'asper_design_library';
import { LandingPage } from './components/LandingPage';
import { listenToSSE } from './services/sseService';
import {
    createStreamingPlaceholder,
    updateLoadingMessage,
    transformChatHistory,
} from './services/messageProcessor';
import {
    getChatHistory,
    mapChatSessionsToThreads,
    deleteThread,
    renameThread,
} from './services/chatHistoryService';
import {
    getCustomQuestions,
    createCustomQuestion,
    updateCustomQuestion,
    deleteCustomQuestion,
    type CustomQuestionBankResponse,
} from './services/customQuestionService';
import {
    getQuestionBankData,
    type QuestionBankCategory,
    toQuestionBankApiFormat,
} from './services/questionBankService';
import { useConversationHistory } from './hooks/useConversationHistory';
import type { AgenticChatMessage, ApiHistoryMessage, CustomQuestionDisplay } from './types';
import type { SSERawPayload } from './types';
import { exportArtifactAsExcel } from './services/exportService';
import { listTenants } from './services/promptService';
import type { Tenant } from './prompts/types';
import { AddToEvalButton } from './eval/AddToEvalButton';
import { notifyAskAsperApiError } from './utils/notifyApiError';

// State retention keys
const ACTIVE_THREAD_ID_KEY = 'AskAsper3_ActiveThreadId';
const SELECTED_THREAD_TITLE_KEY = 'AskAsper3_SelectedThreadTitle';

// Helper functions for state retention
const setAskAsperStoredState = (key: string, value: string | undefined) => {
    if (value !== undefined) {
        localStorage.setItem(key, value);
    } else {
        localStorage.removeItem(key);
    }
};

const getAskAsperStoredState = (key: string): string | undefined => {
    const value = localStorage.getItem(key);
    return value || undefined;
};

const evalNavStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#595959',
    fontFamily: 'inherit',
    lineHeight: 1.4,
};

interface AskAsperProps {
    /** When true, the eval platform UI (prompt branch selector, eval sets, add-to-eval) is shown. */
    isEvalEnabled?: boolean;
}

export const AskAsper: React.FC<AskAsperProps> = ({ isEvalEnabled = false }) => {
    const navigate = useNavigate();
    /** Completed turns (each item = one Q&A with user_input + content/charts/steps) */
    const [messages, setMessages] = useState<AgenticChatMessage[]>([]);
    /** Current streaming turn; layout derives running tasks, phases, accordion from this */
    const [loadingMessage, setLoadingMessage] = useState<AgenticChatMessage | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [activeThreadId, setActiveThreadId] = useState<string | undefined>(undefined);
    const [selectedThreadTitle, setSelectedThreadTitle] = useState<string | undefined>(undefined);
    const [viewMode, setViewMode] = useState<'list' | 'presentation'>('list');
    const [customQuestions, setCustomQuestions] = useState<CustomQuestionBankResponse[]>([]);
    const [questionBankCategories, setQuestionBankCategories] = useState<QuestionBankCategory[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<string | undefined>();
    const {
        details: chatSessions,
        loading: sidebarThreadsLoading,
        refetch: refetchSessions,
        error: sessionsFetchError,
    } = useConversationHistory();
    const threads = mapChatSessionsToThreads(chatSessions);

    useEffect(() => {
        if (sessionsFetchError) notifyAskAsperApiError(sessionsFetchError);
    }, [sessionsFetchError]);

    const loadingMessageRef = useRef<AgenticChatMessage | null>(null);
    const hasRestoredRef = useRef(false);

    const isStreaming = loadingMessage != null;
    const showLandingPage = messages.length === 0 && !loadingMessage;

    const getUserNameFromEmail = (email: string) => {
        const name = email.split('.')[0].charAt(0).toUpperCase() + email.split('.')[0].slice(1);
        return name.includes('@') ? '' : name;
    };

    const userEmail = localStorage.getItem('email') || '';
    const userName = getUserNameFromEmail(userEmail) || '';

    const fetchCustomQuestions = useCallback(async () => {
        if (!userEmail) return;
        try {
            const data = await getCustomQuestions(userEmail);
            setCustomQuestions(data);
        } catch (error) {
            console.error('Failed to fetch custom questions:', error);
            notifyAskAsperApiError(error);
            setCustomQuestions([]);
        }
    }, [userEmail]);

    const fetchQuestionBankData = useCallback(async () => {
        try {
            const data = await getQuestionBankData();
            setQuestionBankCategories(data);
        } catch (error) {
            console.error('Failed to fetch question bank data:', error);
            notifyAskAsperApiError(error);
            setQuestionBankCategories([]);
        }
    }, []);

    useEffect(() => {
        fetchCustomQuestions();
    }, [fetchCustomQuestions]);

    useEffect(() => {
        fetchQuestionBankData();
    }, [fetchQuestionBankData]);

    useEffect(() => {
        if (!isEvalEnabled) return;
        listTenants().then((response) => setTenants(response.tenants)).catch(() => {});
    }, [isEvalEnabled]);

    // Debug: log when question bank data changes (dev only)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && questionBankCategories.length > 0) {
            const total = questionBankCategories.reduce((sum, c) => sum + c.questions.length, 0);
            console.log('[AskAsper] Question bank loaded:', questionBankCategories.length, 'categories,', total, 'questions');
        }
    }, [questionBankCategories]);

    const transformedCustomQuestions = useMemo<CustomQuestionDisplay[]>(
        () =>
            customQuestions.map((q) => ({
                id: q.QuestionID.toString(),
                text: q.Question,
            })),
        [customQuestions],
    );

    const handleAddCustomQuestion = useCallback(
        async (question: { id: string; text: string }) => {
            try {
                await createCustomQuestion({
                    QuestionID: question.id ? parseInt(question.id, 10) : undefined,
                    UserID: userEmail,
                    CustomerID: '-',
                    Question: question.text,
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to add custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions],
    );

    const handleEditCustomQuestion = useCallback(
        async (question: { id?: string | number; text?: string; QuestionID?: number; Question?: string }) => {
            try {
                if (!question) return;

                let questionId: number | null = null;
                let questionText: string | undefined;

                if (question.QuestionID !== undefined && question.QuestionID !== null) {
                    questionId =
                        typeof question.QuestionID === 'number'
                            ? question.QuestionID
                            : parseInt(String(question.QuestionID), 10);
                    questionText = question.Question;
                } else if (question.id !== undefined && question.id !== null) {
                    if (typeof question.id === 'number') {
                        questionId = question.id;
                    } else if (typeof question.id === 'string') {
                        const idStr = question.id.trim();
                        if (idStr === '' || idStr === 'null' || idStr === 'undefined') return;
                        questionId = parseInt(idStr, 10);
                    }
                    questionText = question.text;
                }

                if (questionId === null || isNaN(questionId) || questionId <= 0) return;

                if (!questionText || questionText.trim() === '') {
                    const existingQuestion = customQuestions.find((q) => q.QuestionID === questionId);
                    if (existingQuestion?.Question) {
                        questionText = existingQuestion.Question;
                    } else return;
                }

                await updateCustomQuestion({
                    QuestionID: questionId,
                    UserID: userEmail,
                    CustomerID: '-',
                    Question: questionText.trim(),
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to edit custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions, customQuestions],
    );

    const handleDeleteCustomQuestion = useCallback(
        async (questionId: string) => {
            try {
                await deleteCustomQuestion({
                    QuestionID: parseInt(questionId, 10),
                    UserID: userEmail,
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to delete custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions],
    );

    const handleArtifactExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', run_id?: string, session_id?: string) => {
            if (format !== 'xlsx' || !run_id || !session_id) return;
            const runExport = () => {
                exportArtifactAsExcel(session_id, run_id, `artifact-${run_id}.xlsx`).catch((error) => {
                    console.error('Failed to export artifact as Excel:', error);
                    notifyAskAsperApiError(error, { context: 'export', onRetry: runExport });
                });
            };
            runExport();
        },
        [],
    );

    const handleMultiSelectExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', selectedIds: string[]) => {
            if (format !== 'xlsx' || selectedIds.length === 0 || !activeThreadId) return;
            const sessionId = activeThreadId;
            const runExport = () => {
                Promise.all(
                    selectedIds.map((runId) =>
                        exportArtifactAsExcel(sessionId, runId, `artifact-${runId}.xlsx`),
                    ),
                ).catch((error) => {
                    console.error('Failed to export artifacts as Excel:', error);
                    notifyAskAsperApiError(error, { context: 'export', onRetry: runExport });
                });
            };
            runExport();
        },
        [activeThreadId],
    );

    const handleSearchSubmit = useCallback(
        (query: string) => {
            if (!query.trim() || isStreaming) return;

            const threadIdForRequest = activeThreadId ?? crypto.randomUUID();
            const isNewSession = !activeThreadId;

            if (isNewSession) {
                setActiveThreadId(threadIdForRequest);
                setSelectedThreadTitle(undefined);
            }

            const placeholder = createStreamingPlaceholder(
                query,
                threadIdForRequest
            );
            setLoadingMessage(placeholder);
            loadingMessageRef.current = placeholder;
            setSearchValue('');

            const params = {
                message: query,
                thread_id: threadIdForRequest,
                source: 'EnterpriseData',
                new_session: isNewSession,
                tenant: selectedTenant || undefined,
            };

            const handleMessage = (json: unknown) => {
                const payload = json as SSERawPayload & { type?: string };
                if (payload.type === 'error') {
                    setLoadingMessage((prev) => {
                        if (!prev) return null;
                        const updated = updateLoadingMessage(prev, payload as SSERawPayload);
                        setMessages((m) => [...m, updated]);
                        return null;
                    });
                    loadingMessageRef.current = null;
                    refetchSessions();
                    return;
                }
                setLoadingMessage((prev) => {
                    // Use ref as base when available so rapid events (viz_generation_end, table_generation_end,
                    // task_complete) always apply on top of the latest accumulated state. Prevents race where
                    // React batches updates and a later callback receives stale prev, overwriting earlier changes.
                    const base = loadingMessageRef.current ?? prev;
                    const updated = base ? updateLoadingMessage(base, json as SSERawPayload) : null;
                    loadingMessageRef.current = updated;
                    return updated;
                });
            };

            const handleDone = () => {
                const toAdd = loadingMessageRef.current;
                if (toAdd) setMessages((prev) => [...prev, toAdd]);
                setLoadingMessage(null);
                loadingMessageRef.current = null;
                refetchSessions();
            };

            const handleError = (err: unknown) => {
                console.error('SSE error:', err);
                notifyAskAsperApiError(err);
                setLoadingMessage(null);
                loadingMessageRef.current = null;
            };

            listenToSSE(params, handleMessage, handleDone, handleError);
        },
        [isStreaming, activeThreadId, refetchSessions, selectedTenant]
    );

    // Keep ref in sync for handleDone (closure over latest loadingMessage)
    React.useEffect(() => {
        loadingMessageRef.current = loadingMessage;
    }, [loadingMessage]);

    // Restore stored state on mount
    useEffect(() => {
        const savedThreadId = getAskAsperStoredState(ACTIVE_THREAD_ID_KEY);
        if (savedThreadId) {
            setActiveThreadId(savedThreadId);
            const savedThreadTitle = getAskAsperStoredState(SELECTED_THREAD_TITLE_KEY);
            if (savedThreadTitle) {
                setSelectedThreadTitle(savedThreadTitle);
            }
        }
    }, []);

    // Save activeThreadId when it changes
    useEffect(() => {
        setAskAsperStoredState(ACTIVE_THREAD_ID_KEY, activeThreadId);
    }, [activeThreadId]);

    // Save selectedThreadTitle when it changes
    useEffect(() => {
        setAskAsperStoredState(SELECTED_THREAD_TITLE_KEY, selectedThreadTitle);
    }, [selectedThreadTitle]);

    // Restore chat history when component mounts with a stored activeThreadId
    useEffect(() => {
        if (hasRestoredRef.current || !activeThreadId) return;
        hasRestoredRef.current = true;

        const restoreThread = async () => {
            try {
                const history = await getChatHistory(activeThreadId);
                const transformedMessages = transformChatHistory(
                    history as ApiHistoryMessage[]
                );
                setMessages(transformedMessages);
            } catch (error) {
                console.error('Failed to restore chat history:', error);
                notifyAskAsperApiError(error);
            }
        };

        restoreThread();
    }, [activeThreadId]);

    const handleNewChat = useCallback(() => {
        setMessages([]);
        setLoadingMessage(null);
        loadingMessageRef.current = null;
        setActiveThreadId(undefined);
        setSelectedThreadTitle(undefined);
        setSearchValue('');
        refetchSessions();
    }, [refetchSessions]);

    const handleThreadSelect = useCallback(
        async (threadId: string) => {
            setActiveThreadId(threadId);

            const thread = chatSessions?.find((s) => s.thread_id === threadId);
            if (thread?.thread_name) {
                setSelectedThreadTitle(thread.thread_name);
            }

            try {
                const history = await getChatHistory(threadId);
                const transformedMessages = transformChatHistory(
                    history as ApiHistoryMessage[]
                );
                setMessages(transformedMessages);
            } catch (error) {
                console.error('Failed to fetch chat history:', error);
                notifyAskAsperApiError(error);
                setMessages([]);
            }
        },
        [chatSessions]
    );

    // Handle thread edit (rename)
    const handleThreadEdit = useCallback(
        async (threadId: string, newName?: string) => {
            const session = chatSessions?.find((s) => s.thread_id === threadId);
            if (!session) return;

            // If newName is provided, use it; otherwise skip (component should handle UI)
            if (newName && newName.trim() && newName !== session.thread_name) {
                try {
                    await renameThread({
                        thread_id: threadId,
                        new_thread_name: newName.trim(),
                    });
                    // Update selected thread title if this is the active thread
                    if (activeThreadId === threadId) {
                        setSelectedThreadTitle(newName.trim());
                    }
                    // Refetch conversations after rename
                    await refetchSessions();
                } catch (error) {
                    console.error('Failed to rename thread:', error);
                    notifyAskAsperApiError(error);
                }
            }
        },
        [chatSessions, refetchSessions, activeThreadId],
    );

    // Handle thread delete
    const handleThreadDelete = useCallback(
        async (threadId: string) => {
            try {
                await deleteThread(threadId);
                // Refetch conversations after delete
                await refetchSessions();
                if (activeThreadId === threadId) {
                    setActiveThreadId(undefined);
                    setSelectedThreadTitle(undefined);
                    setMessages([]);
                    setLoadingMessage(null);
                    loadingMessageRef.current = null;
                }
            } catch (error) {
                console.error('Failed to delete thread:', error);
                notifyAskAsperApiError(error);
            }
        },
        [activeThreadId, refetchSessions],
    );

    const handleViewModeChange = useCallback((_mode: 'list' | 'presentation') => {
        // View mode change handler
        setViewMode(_mode);
    }, [setViewMode]);

    const tenantOptions = tenants.flatMap((t) => {
        const opts = [{ value: t.name, label: t.name }];
        (t.drafts ?? []).forEach((d: string) => {
            opts.push({ value: d, label: `${t.name} → ${d}` });
        });
        return opts;
    });

    const [navbarSlot, setNavbarSlot] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!isEvalEnabled) {
            setNavbarSlot(null);
            return undefined;
        }
        // Resolve the navbar slot once it's mounted; retry on the next animation frame if not yet present
        let raf = 0;
        const resolve = () => {
            const el = document.getElementById('navbar-extra-controls');
            if (el) {
                setNavbarSlot(el);
            } else {
                raf = requestAnimationFrame(resolve);
            }
        };
        resolve();
        return () => {
            if (raf) cancelAnimationFrame(raf);
            setNavbarSlot(null);
        };
    }, [isEvalEnabled]);

    const topNavControlsContent = (
        <>
            {tenantOptions.length > 0 && (
                <Select
                    allowClear
                    placeholder="Prompt Branch"
                    style={{ width: 220 }}
                    value={selectedTenant}
                    onChange={setSelectedTenant}
                    options={tenantOptions}
                    showSearch
                    size="small"
                />
            )}
            <button type="button" style={evalNavStyle} onClick={() => navigate('/ask-asper/eval')}>
                <span style={{ fontSize: 15 }}>&#9881;</span>
                Eval Sets
            </button>
        </>
    );

    const topNavControls =
        isEvalEnabled && navbarSlot ? ReactDOM.createPortal(topNavControlsContent, navbarSlot) : null;

    if (showLandingPage) {
        return (
            <>
                {topNavControls}
                <LandingPage
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    onSearchSubmit={handleSearchSubmit}
                    isLoading={false}
                    threads={threads}
                    sidebarThreadsLoading={sidebarThreadsLoading}
                    activeThreadId={activeThreadId}
                    onNewChat={handleNewChat}
                    onThreadSelect={handleThreadSelect}
                    onThreadEdit={handleThreadEdit}
                    onThreadDelete={handleThreadDelete}
                    user={{ name: userName }}
                    customQuestions={transformedCustomQuestions}
                    questionBankCategories={questionBankCategories}
                    onAddCustomQuestion={handleAddCustomQuestion}
                    onEditCustomQuestion={handleEditCustomQuestion}
                    onDeleteCustomQuestion={handleDeleteCustomQuestion}
                />
            </>
        );
    }

    const twoPanelProps = {
        messages,
        loadingMessage,
        onSendMessage: handleSearchSubmit,
        showTopBar: true,
        breadcrumbs: [
            { label: 'Home', href: '/' },
            { label: 'Ask Asper' },
        ],
        showSidebar: true,
        sidebarThreadsLoading,
        sidebarThreads: threads,
        sidebarActiveThreadId: activeThreadId,
        onSidebarNewChat: handleNewChat,
        onSidebarThreadSelect: handleThreadSelect,
        onSidebarThreadEdit: handleThreadEdit,
        onSidebarThreadDelete: handleThreadDelete,
        viewMode,
        showViewModeToggle: true,
        onViewModeChange: handleViewModeChange,
        disableNavbar: true,
        customQuestions: transformedCustomQuestions,
        questionBankCategories:
            questionBankCategories.length > 0
                ? (toQuestionBankApiFormat(questionBankCategories) as any)
                : undefined,
        onAddCustomQuestion: handleAddCustomQuestion,
        onEditCustomQuestion: handleEditCustomQuestion,
        onDeleteCustomQuestion: handleDeleteCustomQuestion,
        onArtifactExport: handleArtifactExport,
        onMultiSelectExport: handleMultiSelectExport,
    };
    return (
        <>
            {topNavControls}
            <AgenticTwoPanelLayout {...(twoPanelProps as any)} />
            {isEvalEnabled && <AddToEvalButton messages={messages} activeThreadId={activeThreadId} />}
        </>
    );
};
